/**
 * Messages dans la page et états d'une donnée : bandeau (information,
 * avertissement, erreur), prochaine étape d'une fiche, erreur de chargement
 * expliquée (accès refusé, introuvable, hors ligne) et enveloppe de requête
 * qui rend chargement, erreur et vide toujours de la même façon.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { AlertTriangle, CircleAlert, Info, Lock, SearchX, WifiOff, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../core/api.js';
import { jourHeureFr } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { Spinner } from '../ui.js';
import { ActionButton, type Action } from './action.js';

/** Résultat d'un geste : fait, mis en attente hors ligne ou refusé. */
export type ActionResultState = 'confirmed' | 'queued' | 'failed';

/** Ton du bandeau qui annonce ce résultat. */
export function resultTone(state: ActionResultState): 'info' | 'warn' | 'danger' {
  return state === 'failed' ? 'danger' : state === 'queued' ? 'warn' : 'info';
}

export function Banner(props: {
  tone?: 'info' | 'warn' | 'danger';
  title?: string;
  children?: ReactNode;
  action?: Action;
  onDismiss?: () => void;
}): ReactNode {
  const tone = props.tone ?? 'info';
  const Icon = tone === 'danger' ? CircleAlert : tone === 'warn' ? AlertTriangle : Info;
  return (
    <div className={`ui-banner ui-banner--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon className="ui-banner__icon" size={20} aria-hidden="true" />
      <div className="ui-banner__text">
        {props.title ? <strong>{props.title}</strong> : null}
        {props.children ? <div>{props.children}</div> : null}
      </div>
      {props.action ? <ActionButton action={props.action} variant="ghost" /> : null}
      {props.onDismiss ? (
        <button type="button" className="btn btn--ghost btn--icon" aria-label="Fermer" onClick={props.onDismiss}>
          <X size={18} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

/** Ce qui attend cette fiche maintenant, avec le geste qui le fait. */
export function NextStepCard(props: {
  title: string;
  description?: ReactNode;
  action?: Action;
  tone?: 'default' | 'attention';
}): ReactNode {
  return (
    <div className={`ui-next-step${props.tone === 'attention' ? ' ui-next-step--attention' : ''}`}>
      <span className="ui-next-step__kicker">Prochaine étape</span>
      <strong className="ui-next-step__title">{props.title}</strong>
      {props.description ? <p className="ui-next-step__text">{props.description}</p> : null}
      {props.action ? <ActionButton action={props.action} /> : null}
    </div>
  );
}

function describe(error: unknown): { icon: ReactNode; title: string; text: string } {
  const code = error instanceof ApiError ? error.code : undefined;
  if (code === 'forbidden') {
    return {
      icon: <Lock size={26} aria-hidden="true" />,
      title: t('erreurs.forbidden'),
      text: t('erreurs.accesRefuseTexte'),
    };
  }
  if (code === 'not_found') {
    return {
      icon: <SearchX size={26} aria-hidden="true" />,
      title: 'Introuvable',
      text: 'Cet élément n’existe plus ou a été déplacé.',
    };
  }
  if (code === 'network_error') {
    return {
      icon: <WifiOff size={26} aria-hidden="true" />,
      title: 'Hors ligne',
      text: 'Les données n’ont pas pu être chargées. Elles s’afficheront au retour du réseau.',
    };
  }
  return {
    icon: <CircleAlert size={26} aria-hidden="true" />,
    title: 'Chargement impossible',
    text: error instanceof Error ? error.message : 'Une erreur est survenue.',
  };
}

/**
 * Signaler un problème à l'équipe MyEquip depuis une erreur (avis sur MyEquip,
 * 24/10/2026). Fourni par la coque de l'application interne seulement : le
 * portail client et les pages publiques n'en ont pas.
 */
export const ReportProblemContext = createContext<(() => void) | undefined>(undefined);

/** Erreur de chargement expliquée, avec « Réessayer » quand c'est utile. */
export function ErrorState(props: { error: unknown; onRetry?: (() => void) | undefined }): ReactNode {
  const { icon, title, text } = describe(props.error);
  const retry = props.onRetry && !(props.error instanceof ApiError && ['forbidden', 'not_found'].includes(props.error.code));
  const reportProblem = useContext(ReportProblemContext);
  // Une panne, pas un refus ni le réseau : elle mérite d'être signalée.
  const report = reportProblem && !(props.error instanceof ApiError
    && ['forbidden', 'not_found', 'network_error'].includes(props.error.code));
  return (
    <div className="empty-state" role="alert">
      {icon}
      <span className="empty-title">{title}</span>
      <span>{text}</span>
      {retry ? (
        <button type="button" className="btn btn--secondary" onClick={props.onRetry}>
          Réessayer
        </button>
      ) : null}
      {report ? (
        <button type="button" className="btn btn--ghost" onClick={reportProblem}>
          {t('avisProduit.signalerErreur')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Page dont la route exige une capacité que le compte n'a pas : l'état est dit
 * (et non un retour silencieux à l'accueil), avec la sortie à un geste.
 */
export function AccessDenied(): ReactNode {
  return (
    <div className="empty-state" role="alert">
      <Lock size={26} aria-hidden="true" />
      <span className="empty-title">{t('erreurs.forbidden')}</span>
      <span>{t('erreurs.accesRefuseTexte')}</span>
      <Link to="/" replace className="btn btn--secondary">
        {t('erreurs.retourAccueil')}
      </Link>
    </div>
  );
}

interface QueryLike<T> {
  data: T | undefined;
  isLoading: boolean;
  error: unknown;
  refetch?: () => unknown;
  /** Lecture en pause faute de réseau (React Query) : hors ligne, pas « une erreur ». */
  isPaused?: boolean;
  /** Heure (ms) de la dernière réponse : l'âge des données gardées à l'écran. */
  dataUpdatedAt?: number;
}

/** Un refus qui vaut aussi pour les données déjà affichées : on ne les montre plus. */
function blocking(error: unknown): boolean {
  return error instanceof ApiError && ['forbidden', 'not_found'].includes(error.code);
}

/**
 * Données gardées à l'écran malgré une lecture qui n'a pas abouti (WEB2-01) :
 * « Hors ligne — données du 13/07 15:22 » sans réseau, « Pas à jour » sur une
 * autre erreur, avec « Réessayer ». Rien de ce qui est affiché ou saisi n'est retiré.
 */
export function StaleDataBanner(props: {
  error: unknown;
  updatedAt?: number | undefined;
  onRetry?: (() => void) | undefined;
}): ReactNode {
  const offline = !props.error || (props.error instanceof ApiError && props.error.code === 'network_error');
  const title = !offline
    ? t('commun.pasAJour')
    : props.updatedAt
      ? t('commun.horsLigneDonneesDu', { date: jourHeureFr(props.updatedAt) })
      : t('commun.horsLigneDonnees');
  return (
    <Banner
      tone="warn"
      title={title}
      {...(props.error && props.onRetry ? { action: { label: t('commun.reessayer'), onClick: props.onRetry } } : {})}
    />
  );
}

/**
 * Chargement, erreur, vide, puis contenu : chaque écran les montre de la même
 * façon. Le chargement est le symbole MyEquip (jamais un texte « Chargement »).
 * Une lecture qui échoue alors que des données sont déjà là les garde, avec un
 * bandeau (WEB2-01) ; sans données, une lecture en pause faute de réseau dit
 * « Hors ligne », jamais « Une erreur est survenue » (WEB1-09).
 */
export function QueryBoundary<T>(props: {
  query: QueryLike<T>;
  loading?: 'page' | 'section';
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}): ReactNode {
  const { query } = props;
  if (query.isLoading) return <Spinner variant={props.loading ?? 'section'} />;
  const retry = query.refetch ? () => void query.refetch?.() : undefined;
  if (query.data === undefined || blocking(query.error)) {
    const error = query.error ?? (query.isPaused ? new ApiError('network_error', t('commun.horsLigneAction')) : undefined);
    return <ErrorState error={error} onRetry={retry} />;
  }
  const stale = query.error || query.isPaused
    ? <StaleDataBanner error={query.error} updatedAt={query.dataUpdatedAt} onRetry={retry} />
    : null;
  if (props.isEmpty?.(query.data)) return <>{stale}{props.empty}</>;
  return <>{stale}{props.children(query.data)}</>;
}
